import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/handleError";
import {IChangePassword, ILoginData, IRegisterData} from "../../../models/iAuth";
import {authAPI} from "../api";
import {MESSAGE_SEVERITY} from "../../../utils/const";
import {setMessage} from "../../messages/model/slice";
import {thunkHandlers} from "../../../store/thunkHandlers";
import {setCurrentUser} from "../../users/model/slice";
import {setCurrentCompany} from "../../companies/model/slice";
import SocketService from "../../../middleware/websocketMiddleware";

// Общий помощник для login/register: сохраняет токен, шлёт success-сообщение,
// тянет профиль и заполняет currentUser/currentCompany. Поведение не менялось.
const handleAuth = async (
    apiMethod: (data: any) => Promise<{ access_token: string }>,
    data: any,
    successMsg: string,
    dispatch: any,
) => {
    const {access_token} = await apiMethod(data);
    if (access_token) {
        localStorage.setItem("access_token", access_token);
        dispatch(setMessage({
            severity: MESSAGE_SEVERITY.success,
            text: successMsg,
        }));
    }
    const currentUser = await authAPI.me();
    dispatch(setCurrentUser(currentUser));
    dispatch(setCurrentCompany(currentUser.company));
    return currentUser;
};

export const fetchLogin = createAsyncThunk(
    "fetch_login",
    async (authData: ILoginData, {dispatch, rejectWithValue}) => {
        try {
            const result = await handleAuth(
                authAPI.login,
                authData,
                "Вы успешно вошли в систему.",
                dispatch,
            );
            // Переподключаем WebSocket с новым токеном (сохранено как было).
            SocketService.reconnectWithNewToken();
            return result;
        } catch (e) {
            // thunkHandlers.error покажет модалку и вернёт текст.
            // После фикса authAPI сюда долетает сырая AxiosError,
            // поэтому "сервер недоступен" обработается корректно.
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchRegister = createAsyncThunk(
    "fetch_register",
    async (registerData: IRegisterData, {dispatch, rejectWithValue}) => {
        try {
            return await handleAuth(
                authAPI.register,
                registerData,
                "Вы успешно зарегистрированы!",
                dispatch,
            );
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchOut = createAsyncThunk(
    "fetch_out",
    async (_, {dispatch, rejectWithValue}) => {
        try {
            await authAPI.out();
            localStorage.removeItem("access_token");
            // Сокет оповещений сидит в комнатах пользователя — рвём его вместе с сессией.
            SocketService.disconnect();
            dispatch(setCurrentUser(null));
            dispatch(setCurrentCompany(null));
            return true;
        } catch (e) {
            // Даже если /logout упал — токен и стор всё равно чистим локально
            // (намеренная защита, сохранена). Сообщение не показываем:
            // юзер уже разлогинен, модалка про ошибку logout была бы лишней.
            localStorage.removeItem("access_token");
            SocketService.disconnect();
            dispatch(setCurrentUser(null));
            dispatch(setCurrentCompany(null));
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchCheckAuth = createAsyncThunk(
    "fetch_check_auth",
    async (_, {dispatch, rejectWithValue}) => {
        try {
            const currentUser = await authAPI.me();
            dispatch(setCurrentUser(currentUser));
            dispatch(setCurrentCompany(currentUser.company));
            return currentUser;
        } catch (e) {
            // Фоновая проверка при старте: модалку НЕ показываем
            // (иначе при недоступном сервере юзер увидит модалку на пустом экране).
            // Тихо реджектим — слайс выставит isAuth=false, isAuthChecked=true.
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchChangePassword = createAsyncThunk(
    "auth/change_password",
    async (changeData: IChangePassword, {dispatch, rejectWithValue}) => {
        try {
            return await authAPI.changePassword(changeData);
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);